import { SearchResponse } from '@algolia/client-search';
import HomeCard from '@components/cards/HomeCard';
import Hero from '@components/hero/Hero';
import awaitUserLogin from '@components/login/awaitUserLogin';
import SearchInput from '@components/search-input/SearchInput';
import UserContext from '@context/UserContext';
import useFacetSearch, { facetSearchServerSide } from '@hooks/useFacetSearch';
import { ContentBlock } from '@layouts/ContentBlocks';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import MuiPagination from '@mui/material/Pagination';
import Typography from '@mui/material/Typography';
import useBreakpoints from '@ui/hooks/useBreakpoints';
import { useStyles } from '@ui/utils/makeStyles';
import { addSavedHome, removeSavedHome } from '@utils/savedItems';
import { makeSeoData } from 'config/next-seo.config';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import React, { useEffect, useMemo } from 'react';
import { useContext } from 'react';

const title = 'Staging a Home is Different with Guest House';
const description = 'When clients choose us, they find out staging a home is different with Guest House. Look at the houses we\'ve staged to see what we mean.';
const vicinity = ['San Diego', 'Coronado', 'Encinitas', 'Norwalk', 'Huntington Beach', 'Anaheim', 'Escondido', 'Mission Viejo'];

export const getServerSideProps: GetServerSideProps = async ({ query }) => {
  const searchResults = await facetSearchServerSide({
    query,
    extraFacetFilters: {
      inStock: undefined,
      vicinity
    }
  });

  return {
    props: {
      query,
      results: JSON.stringify(searchResults),
    },
  };
};

function CaliforniaHomesPage(props: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const router = useRouter();
  const { theme } = useStyles();
  const { md } = useBreakpoints();
  const initResults = useMemo(() => JSON.parse(props.results), [props.results]) as SearchResponse<guesthouse.Home>;
  const userContext = useContext<UserContext>(UserContext);

  const {
    results,
    setQuery,
    page,
    setPage,
    query,
  } = useFacetSearch<guesthouse.Home>({
    query: router.query,
    initResults,
    indexOptions: {
      init: 'homes_install_date_desc',
      offer_price_desc: 'homes',
      offer_price_asc: 'homes',
    },
    extraFacetFilters: {
      inStock: undefined,
      vicinity
    },
    hitsPerPage: 12,
  });

  useEffect(() => {
    if (!router.isReady) {
      return;
    }

    if (Object.keys(router.query).includes('page')) {
      setPage(Number(router.query['page']));
    }
  }, [router.isReady]);

  return (
    <>
      <Head>
        <title>
          {title}
        </title>
      </Head>

      <NextSeo {...makeSeoData({ pageTitle: title, metaTitle: title, metaDescription: description })} />

      <ContentBlock margin={[0, theme.spacing(3)]}>
        <Hero
          key="shop"
          backgroundColor="#ededed"
          backgroundImage="/images/hero-location/hero-california.png"
          style={{
            backgroundPosition: '50% 70%',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            height: md ? 460 : 300,
          }}
        >
          <Box textAlign="center">
            <Typography
              variant="display1"
              component="h1"
              style={{
                color: '#ffffff',
                textShadow: '0px 0px 20px rgba(0, 0, 0, .7)',
                fontSize: md ? 48 : 36,
                marginBottom: theme.spacing(6.5)
              }}
            >
              Explore California homes
            </Typography>
            <Box
              display="flex"
              justifyContent="center"
              width="100%"
            >
              <SearchInput
                placeholder="Enter home address"
                value={query}
                style={{
                  height: md ? 72 : 55,
                  width: md ? 424 : '100%'
                }}
                onValueChange={(value) => {
                  setQuery(value);
                  setPage(0);
                }}
              />
            </Box>
          </Box>
        </Hero>
      </ContentBlock>

      <ContentBlock
        margin={[2, 0, 12]}
        marginMd={[6, 0, 12]}
      >
        <Grid
          container
          spacing={4}
        >
          <Grid
            item
            style={{
              paddingTop: md ? theme.spacing(4.5) : 0,
              position: 'relative'
            }}
          >
            {md &&
              <div
                style={{
                  display: 'flex',
                  flexDirection: md ? 'row' : 'column',
                  alignItems: 'center'
                }}
              >
                <Typography
                  style={{
                    marginLeft: theme.spacing(1),
                    marginRight: theme.spacing(4.5),
                  }}
                >
                  {results.nbHits}
                  {' '}
                  {results.nbHits === 1 ? 'HOME' : 'HOMES'}
                </Typography>
              </div>
            }
            {
              results.hits?.length
                ? (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: md ? 'row' : 'column',
                      justifyContent: md ? 'space-between' : 'center',
                      margin: 'auto'
                    }}
                  >
                    {results?.page + 1 === results?.nbPages ?
                      <Typography style={{ margin: theme.spacing(3, 0), fontSize: 14 }}>
                        Showing
                        {' '}
                        {results.page === 0 ?
                          results.nbHits - (results.nbHits - 1)
                          :
                          results.nbHits - (results.nbHits - (results.hitsPerPage * results.page))
                        }
                        {' '}
                        -
                        {' '}
                        {results.nbHits}
                        {' '}
                        of
                        {' '}
                        {results.nbHits}
                      </Typography>
                      :
                      <Typography style={{ margin: theme.spacing(3, 0), fontSize: 14 }}>
                        Showing
                        {' '}
                        {results.hitsPerPage * (results.page + 1) - (results.hitsPerPage - 1)}
                        {' '}
                        -
                        {' '}
                        {results.hitsPerPage * (results.page + 1)}
                        {' '}
                        of
                        {' '}
                        {results.nbHits}
                      </Typography>
                    }
                    {results.nbPages > 1 &&
                      <div>
                        <MuiPagination
                          size={md ? 'medium' : 'small'}
                          count={results.nbPages}
                          page={page === 0 ? 1 : page}
                          style={{ margin: theme.spacing(3) }}
                          onChange={(e, value: number) => {
                            e.preventDefault();
                            setPage(value);
                          }}
                        />
                      </div>
                    }
                  </div>
                ) : (
                  <Typography style={{ margin: theme.spacing(3, 0) }}>
                    No homes match your search
                  </Typography>
                )
            }

            <Grid
              container
              spacing={1.5}
            >
              {results?.hits?.length > 0 && results.hits.map((home) => {
                const saved = userContext.savedHomes?.ids.indexOf(home.docID) > -1;

                return (
                  <Grid
                    key={home.docID}
                    item
                    xs={6}
                    md={4}
                  >
                    <HomeCard
                      key={home.docID}
                      home={home}
                      saved={saved}
                      onSavedClick={async (e) => {
                        e.stopPropagation();

                        if (userContext.user) {
                          if (saved) {
                            removeSavedHome(userContext.user.uid, home);
                          } else {
                            addSavedHome(userContext.user.uid, home);
                          }
                        } else {
                          await awaitUserLogin(userContext)
                            .then((user) => {
                              addSavedHome(user.uid, home);
                            });
                        }
                      }}
                    />
                  </Grid>
                );
              })}
            </Grid>

            {results.nbPages > 1 &&
              <div>
                <MuiPagination
                  size={md ? 'medium' : 'small'}
                  count={results.nbPages}
                  page={page === 0 ? 1 : page}
                  style={{ margin: theme.spacing(3) }}
                  onChange={(e, value: number) => {
                    e.preventDefault();
                    setPage(value);
                  }}
                />
              </div>
            }

          </Grid>
        </Grid>
      </ContentBlock>
    </>
  );
}

export default CaliforniaHomesPage;
